import { Timestamp, collection, doc, getDocs, limit, orderBy, query, runTransaction, serverTimestamp, startAfter, where } from 'firebase/firestore'
import { auth, db } from '../firebase'

const PAGE_SIZE = 100
const BOT_NAME = 'YiviBot'
export const SUPPORT_STATUSES = ['new', 'preparing', 'fulfilled', 'cancelled']
const millis = (value) => value?.toMillis?.() ?? value?.toDate?.()?.getTime() ?? (value instanceof Date ? value.getTime() : 0)

export function supportStatusOf(report) {
  const status = SUPPORT_STATUSES.includes(report?.supportStatus) ? report.supportStatus : 'new'
  if (!['fulfilled', 'cancelled'].includes(status) || !report?.statusClosedAt) return status
  const closedAt = millis(report.statusClosedAt)
  const newerRequest = (Array.isArray(report.events) ? report.events : []).some((event) => event?.identifier === 'NEED_HELP' && millis(event.receivedAt) > closedAt)
  return newerRequest ? 'new' : status
}
const phoneKey = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : digits
}

function phoneVariants(value) {
  const raw = String(value || '').trim()
  const key = phoneKey(raw)
  if (key.length !== 10) return raw ? [raw] : []
  return [...new Set([raw, raw.replace(/\D/g, ''), key, `0${key}`, `90${key}`, `+90${key}`].filter(Boolean))]
}

function profileDate(value) {
  const date = value?.toDate?.() || (value instanceof Date ? value : new Date(String(value || '')))
  return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime()
}

function profileRank(profile) {
  return [Number(profile.version) || 0, profileDate(profile.updatedAt || profile.createdAt || profile.request_time)]
}

async function enrichWithProfiles(items) {
  const identifiers = [...new Set(items.flatMap((item) => phoneVariants(item.subscriberIdentifier)))]
  if (!identifiers.length) return { items, profileError: false }
  const batches = []
  for (let index = 0; index < identifiers.length; index += 30) batches.push(identifiers.slice(index, index + 30))
  const results = await Promise.allSettled(batches.map((batch) => getDocs(query(
    collection(db, 'populi-datas'), where('telegram_id', 'in', batch),
  ))))
  const profiles = new Map()
  results.forEach((result) => {
    if (result.status !== 'fulfilled') return
    result.value.docs.forEach((snapshot) => {
      const profile = snapshot.data() || {}
      if (profile.botname !== BOT_NAME) return
      const key = phoneKey(profile.telegram_id)
      if (!key) return
      if (!profiles.has(key)) profiles.set(key, [])
      profiles.get(key).push(profile)
    })
  })
  return {
    items: items.map((item) => {
      const matches = (profiles.get(phoneKey(item.subscriberIdentifier)) || []).sort((a, b) => {
        const [versionA, timeA] = profileRank(a)
        const [versionB, timeB] = profileRank(b)
        return versionB - versionA || timeB - timeA
      })
      return {
        ...item,
        subscriberName: matches.find((profile) => String(profile.userName || '').trim())?.userName || '',
        subscriberProvince: matches.find((profile) => String(profile.province || '').trim())?.province || '',
      }
    }),
    profileError: results.some((result) => result.status === 'rejected'),
  }
}

export async function loadSupportReports(cursor = null) {
  const constraints = [orderBy('updatedAt', 'desc'), limit(PAGE_SIZE)]
  if (cursor) constraints.push(startAfter(cursor))
  const snapshot = await getDocs(query(collection(db, 'destekraporlari'), ...constraints))
  const enriched = await enrichWithProfiles(snapshot.docs.map((item) => ({ ...item.data(), id: item.id })))
  return {
    ...enriched,
    cursor: snapshot.docs.length === PAGE_SIZE ? snapshot.docs[snapshot.docs.length - 1] : null,
  }
}

export async function updateSupportStatus(reportId, nextStatus, actor, note = '') {
  if (!reportId || !SUPPORT_STATUSES.includes(nextStatus)) throw new Error('Geçersiz talep durumu.')
  if (!actor?.uid) throw new Error('Durumu değiştirmek için oturum açmanız gerekiyor.')
  if (!auth.currentUser || auth.currentUser.uid !== actor.uid || auth.currentUser.isAnonymous) throw new Error('Oturumunuzu yenileyin.')
  const reportRef = doc(db, 'destekraporlari', reportId)
  const person = {
    uid: actor.uid,
    name: String(actor.nickname || actor.displayName || actor.email || '').slice(0, 150),
    email: String(actor.email || '').slice(0, 200),
  }
  const trimmedNote = String(note || '').trim().slice(0, 500)
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(reportRef)
    if (!snapshot.exists()) throw new Error('Destek talebi artık bulunmuyor.')
    const current = snapshot.data() || {}
    const previousStatus = supportStatusOf(current)
    if (previousStatus === nextStatus) throw new Error('Talep zaten bu durumda.')
    const history = Array.isArray(current.statusHistory) ? current.statusHistory : []
    const change = {
      from: previousStatus,
      to: nextStatus,
      by: person,
      note: trimmedNote,
      at: Timestamp.now(),
    }
    transaction.update(reportRef, {
      supportStatus: nextStatus,
      statusUpdatedAt: serverTimestamp(),
      statusUpdatedBy: person,
      statusNote: trimmedNote,
      statusClosedAt: ['fulfilled', 'cancelled'].includes(nextStatus) ? serverTimestamp() : null,
      statusHistory: [...history, change],
    })
    return {
      supportStatus: nextStatus,
      statusUpdatedAt: change.at,
      statusUpdatedBy: person,
      statusNote: trimmedNote,
      statusClosedAt: ['fulfilled', 'cancelled'].includes(nextStatus) ? change.at : null,
      statusHistory: [...history, change],
    }
  })
}
